import { disposableWindowInterval, registerWindow } from "../../../base/browser/dom";
import { CodeWindow, ensureCodeWindow } from "../../../base/browser/window";
import { Emitter, Event } from "../../../base/common/events";
import { Disposable, IDisposable, toDisposable } from "../../../base/common/lifecycle";

export interface IBaseWindow extends IDisposable {

}



export interface IEditorWindow extends IBaseWindow {

    readonly onDidClose: Event<void>;
    readonly onDidResize: Event<{ width: number; height: number }>;
    readonly onDidMove: Event<{ x: number; y: number }>;

    readonly id: string;
    readonly view: string;

    getWindow(): CodeWindow;

    hasFocus: boolean;
    /** This is the complete window size (not just content size) */
    size: { width: number; height: number; };
    /** Window position on screen */
    position: { x: number; y: number; };
}


export class EditorWindow extends Disposable implements IEditorWindow {

    private readonly _onDidClose = this._register(new Emitter<void>());
    readonly onDidClose: Event<void> = this._onDidClose.event;

    private readonly _onDidResize = this._register(new Emitter<{ width: number; height: number }>());
    readonly onDidResize = this._onDidResize.event;

    private readonly _onDidMove = this._register(new Emitter<{ x: number; y: number }>());
    readonly onDidMove = this._onDidMove.event;

    readonly id: string;
    readonly view: string;

    constructor(
        private readonly window: Window,
        opts: { id:string, view:string },
    ) {
        super();

        this.id = opts.id;
        this.view = opts.view;

        ensureCodeWindow(this.window, Date.now());
        registerWindow(window as CodeWindow);

        this._register(toDisposable(() => {
            this.window.close();
        }));

        this.window.addEventListener('beforeunload', this.onBeforeUnload);
        this._register(toDisposable(() => {
            this.window.removeEventListener('beforeunload', this.onBeforeUnload);
        }));

        this.window.addEventListener('resize', this.onResize);
        this._register(toDisposable(() => {
            this.window.removeEventListener('resize', this.onResize);
        }));

        // when window is moved
        let screenx = this.window.screenX;
        let screeny = this.window.screenY;
        this._register(disposableWindowInterval(this.window, () => {
            if (screenx !== this.window.screenX || screeny !== this.window.screenY) {
                screenx = this.window.screenX;
                screeny = this.window.screenY;
                this.onMove();
            }
        }, 200));
    }

    getWindow(): CodeWindow {
        return this.window as CodeWindow;
    }

    get hasFocus(): boolean {
        return this.window.document.hasFocus();
    }
    get size() {
        return { width: this.window.outerWidth, height: this.window.outerHeight };
    }
    get position() {
        return { x: this.window.screenX, y: this.window.screenY };
    }

    private onBeforeUnload = () => {
        this._onDidClose.fire();
    }

    private onResize = () => {
        this._onDidResize.fire({ width: this.window.innerWidth, height: this.window.innerHeight });
    }

    private onMove = () => {
        this._onDidMove.fire({ x: this.window.screenX, y: this.window.screenY });
    }
}